import type { ApplicabilityRule } from '../../lib/assessment/types'

/**
 * Conservative, layered rules.
 * - Do not encode unverified thresholds as obligations.
 * - Prefer potentially_relevant / further_verification_required over definitive duty claims.
 * - Exchange listing can be “likely relevant” for exchange guidance based on stated listing fact.
 * - UAE climate law MRV duties are never marked likely_relevant from self-assessment alone.
 */
export const APPLICABILITY_RULES: ApplicabilityRule[] = [
  // ── Exchange guidance ───────────────────────────────────────
  {
    id: 'rule_dfm_listed',
    referenceId: 'dfm_esg_guide',
    conditions: [{ field: 'listingOrExchange', includesAny: ['dfm', 'both'] }],
    resultIfMatched: 'likely_relevant',
    notes:
      'DFM listing indicated by respondent. Confirm current DFM ESG guide expectations for the issuer category on the official DFM site.',
  },
  {
    id: 'rule_adx_listed',
    referenceId: 'adx_esg_overview',
    conditions: [{ field: 'listingOrExchange', includesAny: ['adx', 'both'] }],
    resultIfMatched: 'likely_relevant',
    notes:
      'ADX listing indicated by respondent. Confirm current ADX sustainability disclosure expectations on the official ADX site.',
  },
  {
    id: 'rule_adx_metrics_listed',
    referenceId: 'adx_esg_metrics',
    conditions: [{ field: 'listingOrExchange', includesAny: ['adx', 'both'] }],
    resultIfMatched: 'likely_relevant',
    notes: 'ADX listing indicated. Review official ADX ESG metrics for current issuer-category applicability.',
  },
  {
    id: 'rule_ipo_dfm',
    referenceId: 'dfm_esg_guide',
    conditions: [{ field: 'ipo', equals: 'yes' }],
    resultIfMatched: 'further_verification_required',
    notes:
      'IPO preparation may increase exchange disclosure readiness needs. Verify the target market (DFM/ADX/other) and timeline before treating exchange guidance as applicable.',
  },
  {
    id: 'rule_ipo_adx',
    referenceId: 'adx_esg_overview',
    conditions: [{ field: 'ipo', equals: 'yes' }],
    resultIfMatched: 'further_verification_required',
    notes:
      'IPO preparation may increase ADX-related disclosure readiness needs if ADX is the target market. Verify listing plan before concluding applicability.',
  },

  // ── UAE Federal Decree-Law No. 11 of 2024 (layered) ─────────
  {
    id: 'rule_uae_climate_law_presence',
    referenceId: 'uae_fdl_11_2024',
    conditions: [{ field: 'operatesInUae', equals: 'yes' }],
    resultIfMatched: 'potentially_relevant',
    notes:
      'UAE operating presence indicated. The Decree-Law applies to sources in the State, including free zones. This is not confirmation that your entity is a designated Source for detailed MRV duties.',
  },
  {
    id: 'rule_uae_climate_law_emission_profile',
    referenceId: 'uae_fdl_11_2024',
    conditions: [
      { field: 'operatesInUae', equals: 'yes' },
      { field: 'likelyEmitterProfile', equals: 'yes' },
    ],
    resultIfMatched: 'potentially_relevant',
    notes:
      'Sector/activity answers suggest operational GHG sources may exist. Still verify whether the entity is a Source and whether the Ministry/competent authority has determined it for measurement, inventory and reporting duties.',
  },
  {
    id: 'rule_uae_climate_law_designation_yes',
    referenceId: 'uae_fdl_11_2024',
    conditions: [{ field: 'designatedMrv', equals: 'yes' }],
    resultIfMatched: 'further_verification_required',
    notes:
      'Respondent reported designation/notification for emissions measurement, inventory or reporting. Professional review of the notice, competent authority and exact duties is required — this tool cannot validate designation.',
  },
  {
    id: 'rule_uae_climate_law_designation_unsure',
    referenceId: 'uae_fdl_11_2024',
    conditions: [
      { field: 'operatesInUae', equals: 'yes' },
      { field: 'designatedMrv', equals: 'not_sure' },
    ],
    resultIfMatched: 'further_verification_required',
    notes:
      'Designation status is uncertain. Confirm with internal legal/compliance owners and the relevant competent authority whether any MRV determination applies. Do not assume non-applicability from uncertainty.',
  },
  {
    id: 'rule_uae_climate_law_authority_notice',
    referenceId: 'uae_fdl_11_2024',
    conditions: [{ field: 'authorityNotice', equals: 'yes' }],
    resultIfMatched: 'further_verification_required',
    notes:
      'A specific authority notice was reported. Review the notice text, issuing authority and any deadlines with qualified advisors.',
  },
  {
    id: 'rule_uae_climate_law_multi_emirate',
    referenceId: 'uae_fdl_11_2024',
    conditions: [
      { field: 'operatesInUae', equals: 'yes' },
      { field: 'multiEmirate', equals: 'yes' },
    ],
    resultIfMatched: 'further_verification_required',
    notes:
      'Multi-emirate operations may involve more than one competent authority context. Verify coordination expectations; do not assume a single emirate process covers all sites.',
  },
  {
    id: 'rule_moccae_portal_uae',
    referenceId: 'moccae_portal',
    conditions: [{ field: 'operatesInUae', equals: 'yes' }],
    resultIfMatched: 'potentially_relevant',
    notes:
      'MOCCAE portal is a practical starting point for current federal climate/environment instruments. Portal relevance is informational, not an entity-specific duty.',
  },

  // ── Market / voluntary ──────────────────────────────────────
  {
    id: 'rule_customer_expectations',
    referenceId: 'customer_supply_chain_esg',
    conditions: [
      { field: 'customerRequests', includesAny: ['yes', 'frequent', 'occasional', 'anticipated'] },
    ],
    resultIfMatched: 'voluntary_or_market_driven',
    notes:
      'Customer or supply-chain ESG requests indicated. Treat as market-driven unless a contract creates a specific obligation.',
  },
  {
    id: 'rule_multinational_supply',
    referenceId: 'customer_supply_chain_esg',
    conditions: [{ field: 'multinationalSupply', equals: 'yes' }],
    resultIfMatched: 'voluntary_or_market_driven',
    notes:
      'Supplying multinational customers often triggers cascading ESG questionnaires even without a UAE statutory reporting duty.',
  },
  {
    id: 'rule_lender_expectations',
    referenceId: 'lender_esg_expectations',
    conditions: [{ field: 'lenderRequests', includesAny: ['yes', 'anticipated'] }],
    resultIfMatched: 'voluntary_or_market_driven',
    notes: 'Lender ESG information requests indicated. Confirm what the facility agreement or due-diligence pack actually requires.',
  },
  {
    id: 'rule_parent_reporting',
    referenceId: 'customer_supply_chain_esg',
    conditions: [{ field: 'parentReporting', equals: 'yes' }],
    resultIfMatched: 'voluntary_or_market_driven',
    notes:
      'Parent-company reporting requests indicated. Group requirements can create practical disclosure duties without being UAE legislation.',
  },
  {
    id: 'rule_eu_export_cbam',
    referenceId: 'eu_cbam_market',
    conditions: [{ field: 'exportEu', equals: 'yes' }],
    resultIfMatched: 'voluntary_or_market_driven',
    notes:
      'EU / carbon-related export exposure indicated. CBAM and customer data requests may be relevant for specific goods and roles — verify product coverage; this is not UAE law.',
  },
  {
    id: 'rule_contractual_emissions',
    referenceId: 'customer_supply_chain_esg',
    conditions: [{ field: 'contractualEmissions', equals: 'yes' }],
    resultIfMatched: 'voluntary_or_market_driven',
    notes:
      'Contractual emissions-reporting commitments indicated. Review the contract clauses for scope, frequency and evidence requirements.',
  },
  {
    id: 'rule_ghg_protocol_inventory',
    referenceId: 'ghg_protocol',
    conditions: [{ field: 'hasGhgInventory', equals: 'yes' }],
    resultIfMatched: 'voluntary_or_market_driven',
    notes:
      'Existing GHG inventory indicated. GHG Protocol is a common methodology reference for inventory quality and boundary documentation.',
  },
  {
    id: 'rule_ghg_protocol_default',
    referenceId: 'ghg_protocol',
    conditions: [{ field: 'operatesInUae', equals: 'yes' }],
    resultIfMatched: 'voluntary_or_market_driven',
    notes:
      'Shown as a voluntary methodology reference for companies building or improving a UAE operational GHG inventory.',
  },
  {
    id: 'rule_gri_selected',
    referenceId: 'gri_standards',
    conditions: [{ field: 'framework', includesAny: ['gri', 'multiple'] }],
    resultIfMatched: 'voluntary_or_market_driven',
    notes: 'Respondent indicated GRI use. GRI remains a voluntary framework unless adopted into a binding requirement.',
  },
  {
    id: 'rule_ifrs_selected',
    referenceId: 'ifrs_sustainability',
    conditions: [{ field: 'framework', includesAny: ['ifrs', 'multiple'] }],
    resultIfMatched: 'voluntary_or_market_driven',
    notes:
      'Respondent indicated IFRS Sustainability Disclosure Standards use. Confirm whether adoption is voluntary, parent-driven or jurisdiction-mandated.',
  },
  {
    id: 'rule_ifrs_listed',
    referenceId: 'ifrs_sustainability',
    conditions: [{ field: 'listingOrExchange', includesAny: ['adx', 'dfm', 'both'] }],
    resultIfMatched: 'further_verification_required',
    notes:
      'Listed issuer context can increase interest in ISSB-aligned disclosures. Verify whether any exchange, parent or jurisdiction expectation applies — ISSB standards are not automatically UAE law.',
  },
]
